import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import moment from "moment";
import axios from "axios";
import ActionBar from "@/components/core/ActionBar";
import Pagination from "@/components/core/Pagination";
import { APIService } from "@/common/API";
import Settings from "@/common/Settings";
import SuperAdminBackLink from "@/components/platform/SuperAdminBackLink";
import { parsedUser } from "@/common/GetCurrentUser";
import { isSuperAdmin } from "@/common/helpers";

interface ActivityLogRecord {
  id: string;
  userId: string | null;
  userName: string | null;
  userImage: string | null;
  section: string | null;
  action: string | null;
  content: string | null;
  itemId: string | null;
  date: string | null;
}

interface Filters {
  organizationId: string;
  search: string;
  section: string;
  action: string;
  dateFrom: string;
  dateTo: string;
}

interface OrganizationOption {
  id: string;
  name: string;
}

const ACTION_BADGE_MAP: Record<string, string> = {
  Create: "success",
  Update: "warning",
  Delete: "danger",
  Login: "info",
};

interface ActivityLogPageProps {
  embedded?: boolean;
}

const ActivityLogPage = ({ embedded = false }: ActivityLogPageProps) => {
  const { t } = useTranslation("global");
  const user: any = parsedUser();
  const canSelectOrganization = isSuperAdmin(user);
  const userOrgId = user?.OrganizationId || user?.organizationId || "";

  const [records, setRecords] = useState<ActivityLogRecord[]>([]);
  const [sections, setSections] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const QUANTITY = 25;

  const [filters, setFilters] = useState<Filters>({
    organizationId: canSelectOrganization ? "" : userOrgId,
    search: "",
    section: "",
    action: "",
    dateFrom: "",
    dateTo: "",
  });

  const buildQueryString = (page: number, f: Filters) => {
    const params: string[] = [`Page=${page}`, `Quantity=${QUANTITY}`];
    if (f.organizationId) params.push(`OrganizationId=${encodeURIComponent(f.organizationId)}`);
    if (f.search.trim()) params.push(`Search=${encodeURIComponent(f.search.trim())}`);
    if (f.section) params.push(`Section=${encodeURIComponent(f.section)}`);
    if (f.action) params.push(`Action=${encodeURIComponent(f.action)}`);
    if (f.dateFrom) params.push(`DateFrom=${encodeURIComponent(f.dateFrom)}`);
    if (f.dateTo) params.push(`DateTo=${encodeURIComponent(f.dateTo)}`);
    return params.join("&");
  };

  const bindData = async (page = 0, f = filters) => {
    try {
      setIsLoading(true);
      const res = await APIService.getAction("ActivityLog/List", buildQueryString(page, f));
      if (res.data.status === "ok") {
        setRecords(res.data.response || []);
        setTotalPages(res.data.quantityOfPages);
        setTotalRecords(res.data.quantityOfRecords);
        setCurrentPage(page);
      }
    } catch (ex) {
      console.error(ex);
    } finally {
      setIsLoading(false);
    }
  };

  const bindDropdowns = async () => {
    try {
      const organizationQs = filters.organizationId ? `OrganizationId=${encodeURIComponent(filters.organizationId)}` : undefined;
      const [secRes, actRes] = await Promise.all([
        APIService.getAction("ActivityLog/Sections", organizationQs),
        APIService.getAction("ActivityLog/Actions", organizationQs),
      ]);
      if (secRes.data.status === "ok") setSections(secRes.data.response || []);
      if (actRes.data.status === "ok") setActions(actRes.data.response || []);
    } catch (ex) {
      console.error(ex);
    }
  };

  const loadOrganizations = async () => {
    if (!canSelectOrganization) return;

    try {
      setIsLoadingOrganizations(true);
      const res = await APIService.getAction("Organization/AdminList", "Page=0&Quantity=200");
      if (res.data.status === "ok") {
        setOrganizations(res.data.response || []);
      }
    } catch (ex) {
      console.error(ex);
    } finally {
      setIsLoadingOrganizations(false);
    }
  };

  useEffect(() => {
    loadOrganizations();
    bindData();
    bindDropdowns();
  }, []);

  useEffect(() => {
    bindDropdowns();
    bindData(0, filters);
  }, [filters.organizationId]);

  const handleSearch = () => {
    bindData(0, filters);
  };

  const handleClear = () => {
    const cleared: Filters = {
      organizationId: canSelectOrganization ? "" : userOrgId,
      search: "",
      section: "",
      action: "",
      dateFrom: "",
      dateTo: "",
    };
    setFilters(cleared);
    bindData(0, cleared);
  };

  const handlePageChange = (page: number) => {
    bindData(page, filters);
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const params: string[] = [];
      if (filters.search.trim()) params.push(`Search=${encodeURIComponent(filters.search.trim())}`);
      if (filters.section) params.push(`Section=${encodeURIComponent(filters.section)}`);
      if (filters.action) params.push(`Action=${encodeURIComponent(filters.action)}`);
      if (filters.dateFrom) params.push(`DateFrom=${encodeURIComponent(filters.dateFrom)}`);
      if (filters.dateTo) params.push(`DateTo=${encodeURIComponent(filters.dateTo)}`);
      const token = localStorage.getItem("LogedUser");
      const url = `${Settings.BasePathForAPI}ActivityLog/Export${params.length ? "?" + params.join("&") : ""}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const href = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = href;
      link.download = `activity_log_${moment().format("YYYYMMDD_HHmm")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } catch (ex) {
      console.error(ex);
    } finally {
      setIsExporting(false);
    }
  };

  const getActionBadge = (action: string | null) => {
    const variant = ACTION_BADGE_MAP[action ?? ""] ?? "secondary";
    return <Badge bg={variant}>{action}</Badge>;
  };

  const content = (
    <>
      {!embedded && <SuperAdminBackLink />}

      <ActionBar
        title={t("Activity Log")}
        icon="fa fa-history me-2"
        actions={
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? (
              <Spinner as="span" animation="border" size="sm" className="me-1" />
            ) : (
              <i className="fa fa-download me-1" />
            )}
            {t("Export CSV")}
          </Button>
        }
      />

      <Card className="mt-3">
        <Card.Body className="pb-2">
          <Row className="g-2">
            {canSelectOrganization && (
              <Col xs={12} md={3}>
                <Form.Select
                  size="sm"
                  value={filters.organizationId}
                  onChange={e => setFilters(f => ({ ...f, organizationId: e.target.value }))}
                  disabled={isLoadingOrganizations}
                >
                  <option value="">{t("All Organizations")}</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </Form.Select>
              </Col>
            )}
            <Col xs={12} md={4}>
              <Form.Control
                size="sm"
                placeholder={t("Search...")}
                value={filters.search}
                onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                onKeyDown={e => e.key === "Enter" && handleSearch()}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={filters.section}
                onChange={e => setFilters(f => ({ ...f, section: e.target.value }))}
              >
                <option value="">{t("All Sections")}</option>
                {sections.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={filters.action}
                onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}
              >
                <option value="">{t("All Actions")}</option>
                {actions.map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Control
                size="sm"
                type="date"
                value={filters.dateFrom}
                onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                title={t("Date From")}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Control
                size="sm"
                type="date"
                value={filters.dateTo}
                onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                title={t("Date To")}
              />
            </Col>
          </Row>
          <Row className="mt-2">
            <Col xs="auto">
              <Button size="sm" variant="primary" onClick={handleSearch} disabled={isLoading}>
                <i className="fa fa-search me-1" />
                {t("Search")}
              </Button>
              <Button size="sm" variant="link" className="ms-2" onClick={handleClear}>
                {t("Clear")}
              </Button>
            </Col>
            {isLoading && (
              <Col xs="auto" className="d-flex align-items-center">
                <Spinner animation="border" size="sm" />
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      <Card className="mt-3">
        <Card.Body className="p-0">
          {records.length === 0 && !isLoading ? (
            <div className="text-center text-muted py-5">
              <i className="fa fa-history fa-2x mb-2 d-block" />
              {t("No activity records found")}
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0" size="sm">
                <thead className="table-light">
                  <tr>
                    <th>{t("Date")}</th>
                    <th>{t("User")}</th>
                    <th>{t("Section")}</th>
                    <th>{t("Action")}</th>
                    <th>{t("Content")}</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(record => (
                    <tr key={record.id}>
                      <td className="text-nowrap small">
                        <span title={moment(record.date).format("YYYY-MM-DD HH:mm:ss")}>
                          {moment(record.date).fromNow()}
                        </span>
                        <br />
                        <small className="text-muted">{moment(record.date).format("MMM D, YYYY")}</small>
                      </td>
                      <td className="small">{record.userName || "—"}</td>
                      <td className="small">{record.section}</td>
                      <td>{getActionBadge(record.action)}</td>
                      <td className="small text-muted">{record.content}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
        {totalPages > 1 && (
          <Card.Footer>
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChanged={handlePageChange}
              totalRecords={totalRecords}
            />
          </Card.Footer>
        )}
      </Card>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Container className="py-4">
      {content}
    </Container>
  );
};

export default ActivityLogPage;
